import store, { waitForCart, waitForUser } from '../../store/store';
import { ActionStatus } from '../../common/constants/action-status';
import { BaseComponent } from '../base-component/base-component';
import { CART_MESSAGE_ID, updateGlobalMessage } from '../alert/global-message';
import { MessageType } from '../alert/global-message.interface';
import { RmsBookstoreAddress } from '../checkout/shop-checkout.interface';
import { ShopCart } from '../cart/shop-cart/interfaces/shop-cart.interface';
import { StoreState } from '../../store/store.interface';
import { connect } from 'pwa-helpers';
import { dispatchBraintreeClientToken, ERROR_ADDRESS_VALIDATION_MESSAGE } from './utils/braintree.util';
import { getValidatedAddress } from '../address/utils/address.util';
import { getAddressValidationError, getAddressValidationStatus } from '../address/store/address-validation';
import {
  getBraintreeClientTokenApiStatus,
  getTotalPrice,
  isPurchaseOrderSelected,
  resetPaymentDetails,
  updateActiveStep,
  updateCountryCodeCurrencyCode
} from '../checkout/shop-checkout';
import { getCart, getCartStatus, isCartValid } from '../cart/shop-cart/store/shop-cart.selectors';
import { getCheckoutUrl } from '../../common/utils/cart-util';
import { getShopCart } from '../cart/shop-cart/thunks/get-shop-cart.thunk';
import { handleApiError } from '../../common/utils/api-utils';
import { isFailureUser, isSuccessUser } from '../user-profile/user-profile.selectors';
import { property, state } from 'lit/decorators.js';

/**
 * Template to be implemented by every cart payment method.
 */
export declare interface CartPaymentMethodTemplate {

  /**
   * Verifies if the dependencies for the Braintree payment method are being loaded.
   */
  isLoadingDependencies(): boolean;

  /**
   * Verifies if the payment method is enabled.
   * @param _state
   */
  isPaymentMethodEnabled(_state: StoreState): boolean;

  /**
   * Action to be executed when the payment method processing is complete.
   */
  terminatePaymentMethodProcessing(): void;

  /**
   * Update shipping address in the store.
   * @param address
   */
  updateBillingAddress(address: RmsBookstoreAddress): void;

  /**
   * Update shipping address in the store.
   * @param address
   */
  updateShippingAddress(address: RmsBookstoreAddress): void;
}

/**
 * Parent class of cart payment methods. Contains common billing address logic.
 */
export class CartPaymentMethod extends connect(store)(BaseComponent) {
  /**
   * The store to checkout to. If not set the national checkout will be used.
   */
  @property()
  store?: string

  /**
   * Braintree client token to initialize the payment method button.
   */
  @state()
  protected clientToken = '';

  /**
   * Currency for Braintree transaction.
   */
  @state()
  protected currencyCode = 'USD';

  /**
   * Country code of the order. Default US.
   */
  @state()
  protected countryCode = 'US';

  /**
   * Flag to indicate if the cart is valid.
   */
  protected isCartValid = false;

  /**
   * Flag to indicate that the component is loading.
   */
  @state()
  protected isLoading = true;

  /**
   * Flag to indicate the checkout contains shippable items.
   */
  @state()
  protected shippable = false;

  /**
   * Flag to indicate wether the payment button is visible or not.
   */
  @state()
  protected showPaymentButton = false;

  /**
   * Total amount of the order.
   */
  @state()
  protected totalAmount = 0;

  connectedCallback() {
    super.connectedCallback();
    this._storeUnsubscribe = store.subscribe(
      () => this._stateChanged(store.getState() as StoreState)
    );
  }

  /**
   * When the Braintree payment method loads on cart page, check if user is logged-in.
   * Set country and currency codes.
   * Validate cart.
   * If cart is valid get Braintree client token.
   * @param _changedProperties
   */
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    store.dispatch(updateCountryCodeCurrencyCode({
      currencyCode: this.currencyCode,
      countryCode: this.countryCode,
    }));
    store.dispatch(getShopCart({}));
    await waitForUser();
    await waitForCart();
    if (isSuccessUser(store.getState() as StoreState) && isCartValid(store.getState() as StoreState)) {
      dispatchBraintreeClientToken(this.countryCode, this.currencyCode);
    }
  }

  /**
   * If user is logged out, do not show the payment method button.
   * When Braintree client token response is successful, update client token and showPaymentButton flag.
   * Whenever cart is updated, check if cart is valid.
   * If cart is valid, update the payment method button with latest amount.
   * Then, update cart shippable flag and update showPaymentButton flag.
   * @param state
   */
  _stateChanged(state: StoreState) {
    if (getAddressValidationStatus(state) === ActionStatus.failed) {
      handleApiError(getAddressValidationError(state));
      return;
    }
    this.isLoading = this.isLoadingDependencies();
    waitForUser().then(() => {
      if (isFailureUser(state)) {
        this.isLoading = false;
        this.showPaymentButton = false;
      } else if (isSuccessUser(state)) {
        if (getBraintreeClientTokenApiStatus(state) === ActionStatus.failed) {
          this.showPaymentButton = false;
          return;
        }
        if (getCartStatus(state) === ActionStatus.succeeded) {
          const cart: ShopCart = getCart(state)!;
          this.isCartValid = isCartValid(state);
          if (this.isCartValid) {
            this.totalAmount = getTotalPrice(state)!;
            this.shippable = cart.shippable;
          }
          this.clientToken = state.shopCheckoutState.clientToken.data?.token || "";
          this.showPaymentButton = this.isPaymentMethodEnabled(state) && this.isCartValid && !isPurchaseOrderSelected(state);
          this.checkBraintreeClientToken();
        } else if (getCartStatus(state) === ActionStatus.failed) {
          this.showPaymentButton = false;
          return;
        }
      }
    });
  }

  /**
   * Checks if Braintree client token is available, otherwise it refreshes it.
   * @protected
   */
  protected checkBraintreeClientToken() {
    if (!this.isLoading && this.isCartValid && this.clientToken === "") {
      dispatchBraintreeClientToken(this.countryCode, this.currencyCode);
    }
  }

  /**
   * Collects payment information and proceeds to checkout page.
   *
   * @param event
   * @protected
   */
  protected async onCollectPaymentInfo(event: CustomEvent): Promise<void> {
    try {
      const billingAddress: RmsBookstoreAddress | undefined = await getValidatedAddress(event.detail.billingAddress);
      this.updateBillingAddress(billingAddress);
    } catch (error) {
      return this.dispatchPaymentAddressError();
    }

    if (this.shippable) {
      try {
        const shippingAddress: RmsBookstoreAddress | undefined = await getValidatedAddress(event.detail.shippingAddress);
        this.updateShippingAddress(shippingAddress);
      } catch (error) {
        return this.dispatchPaymentAddressError();
      }
    }

    // Go to checkout page
    const checkoutUrl = getCheckoutUrl(store.getState() as StoreState, this.store);
    if (checkoutUrl) {
      this.terminatePaymentMethodProcessing();
      store.dispatch(updateActiveStep(undefined));
      location.href = checkoutUrl;
    }
  }

  /**
   * Resets the payment details and displays the address error message when the street validation fails.
   *
   * @private
   */
  protected dispatchPaymentAddressError(): void {
    store.dispatch(resetPaymentDetails());
    store.dispatch(updateGlobalMessage({
      id: CART_MESSAGE_ID,
      message: ERROR_ADDRESS_VALIDATION_MESSAGE,
      type: MessageType.primary
    }));
    this.terminatePaymentMethodProcessing();
  }

  /**
   * Verifies if the dependencies for the Braintree payment method are being loaded.
   * To be overridden by children classes.
   */
  isLoadingDependencies(): boolean {
    throw new Error('Method not implemented.');
  }

  /**
   * Verifies if the payment method is enabled.
   * To be overridden by children classes.
   * @param _state
   */
  isPaymentMethodEnabled(_state: StoreState): boolean {
    throw new Error('Method not implemented.');
  }

  /**
   * Updates billing address fields in the store.
   * To be overridden by children classes.
   */
  updateBillingAddress(_address: RmsBookstoreAddress): void {
    throw new Error('Method not implemented.');
  }

  /**
   * Updates shipping address fields in the store.
   * To be overridden by children classes.
   */
  updateShippingAddress(_address: RmsBookstoreAddress): void {
    throw new Error('Method not implemented.');
  }

  /**
   * Action to be executed when the payment method processing is complete.
   * To be overridden by children classes.
   */
  terminatePaymentMethodProcessing(): void {
    throw new Error('Method not implemented.');
  }
}
